/**
 * Cache Helper Utility
 * 
 * Helps manage browser cache and service worker cache to resolve auth issues
 */

/**
 * Clear all browser storage and caches
 */
export const clearAllCache = async (): Promise<void> => {
  try {
    // Clear localStorage
    localStorage.clear();
    
    // Clear sessionStorage
    sessionStorage.clear();
    
    // Clear service worker caches if available
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map(cacheName => caches.delete(cacheName))
      );
      console.log('Service worker caches cleared');
    }
    
    // Clear IndexedDB if needed (for Supabase storage)
    if ('indexedDB' in window) {
      try {
        // Clear Supabase auth storage
        const deleteDB = (dbName: string) => {
          return new Promise<void>((resolve, reject) => {
            const deleteReq = indexedDB.deleteDatabase(dbName);
            deleteReq.onerror = () => reject(deleteReq.error);
            deleteReq.onsuccess = () => resolve();
          });
        };
        
        await deleteDB('supabase-cache');
        console.log('IndexedDB cleared');
      } catch (error) {
        console.warn('Could not clear IndexedDB:', error);
      }
    }
    
    console.log('All caches cleared successfully');
  } catch (error) {
    console.error('Error clearing caches:', error);
  }
};

/**
 * Clear only auth-related storage
 */
export const clearAuthCache = (): void => {
  try {
    // Clear auth-related localStorage items
    const authKeys = Object.keys(localStorage).filter(key => 
      key.includes('auth') || 
      key.includes('supabase') || 
      key.includes('subscription') ||
      key.includes('token')
    );
    
    authKeys.forEach(key => localStorage.removeItem(key));
    
    // Clear auth-related sessionStorage
    const sessionAuthKeys = Object.keys(sessionStorage).filter(key => 
      key.includes('auth') || 
      key.includes('supabase') || 
      key.includes('subscription') ||
      key.includes('token')
    );
    
    sessionAuthKeys.forEach(key => sessionStorage.removeItem(key));
    
    console.log('Auth cache cleared');
  } catch (error) {
    console.error('Error clearing auth cache:', error);
  }
};

/**
 * Force refresh the page with cache bypass
 */
export const forceRefresh = (): void => {
  console.log('Force refreshing page with cache bypass');
  window.location.reload();
};

/**
 * Clear cache and refresh
 */
export const clearCacheAndRefresh = async (): Promise<void> => {
  console.log('Clearing cache and refreshing page');
  await clearAllCache();
  forceRefresh();
};

/**
 * Clear only auth cache and refresh
 */
export const clearAuthCacheAndRefresh = (): void => {
  console.log('Clearing auth cache and refreshing page');
  clearAuthCache();
  forceRefresh();
};

/**
 * Check if there are potential cache issues
 */
export const detectCacheIssues = (): {
  hasOldAuthTokens: boolean;
  hasMultipleAuthSessions: boolean;
  hasCorruptedStorage: boolean;
  recommendations: string[];
} => {
  const recommendations: string[] = [];
  let hasOldAuthTokens = false;
  let hasMultipleAuthSessions = false;
  let hasCorruptedStorage = false;
  
  try {
    // Check for old auth tokens
    const localStorageAuthKeys = Object.keys(localStorage).filter(key => 
      key.includes('auth') || key.includes('token')
    );
    
    if (localStorageAuthKeys.length > 3) {
      hasOldAuthTokens = true;
      recommendations.push('Clear old auth tokens from localStorage');
    }
    
    // Check for multiple auth sessions
    const authSessions = Object.keys(localStorage).filter(key => 
      key.includes('supabase') && key.includes('auth')
    );
    
    if (authSessions.length > 1) {
      hasMultipleAuthSessions = true;
      recommendations.push('Clear multiple auth sessions');
    }
    
    // Check for corrupted storage
    try {
      authSessions.forEach(key => {
        const value = localStorage.getItem(key);
        if (value) {
          JSON.parse(value);
        }
      });
    } catch (parseError) {
      hasCorruptedStorage = true;
      recommendations.push('Clear corrupted storage data');
    }
    
    if (recommendations.length === 0) {
      recommendations.push('No cache issues detected');
    }
    
  } catch (error) {
    hasCorruptedStorage = true;
    recommendations.push('Storage access error - consider clearing all cache');
  }
  
  return {
    hasOldAuthTokens,
    hasMultipleAuthSessions,
    hasCorruptedStorage,
    recommendations
  };
};

// Make utilities available globally for debugging
if (typeof window !== 'undefined') {
  (window as any).cacheHelper = {
    clearAll: clearAllCache,
    clearAuth: clearAuthCache,
    refresh: forceRefresh,
    clearAndRefresh: clearCacheAndRefresh,
    clearAuthAndRefresh: clearAuthCacheAndRefresh,
    detect: detectCacheIssues
  };
} 